import type { FaqContent } from "../types";

export const faq: FaqContent = {
  title: "FAQ",
  categoriesAriaLabel: "FAQ categories",
  groups: [
    {
      label: "Getting started",
      items: [
        {
          q: "Who can use UCloud AI?",
          a: "Every researcher and student at Denmark's eight universities. Access uses your standard university login, with no external API keys. AI usage requires credits from your university's front office, requested via a lightweight application form directly on UCloud. If you already use UCloud, UCloud AI is already part of your project.",
        },
        {
          q: "Do I need to install anything?",
          a: "No. The integrated chat interface runs in the browser inside UCloud, so you can start using the models right away. For everything else, the OpenAI-compatible API lets you bring your own tools: research code that already speaks to OpenAI-style endpoints keeps working unchanged.",
        },
        {
          q: "What does it cost?",
          a: "Usage is accounted in credits on the national UCloud platform. All universities have access and can purchase credits for AI usage as they need them. There are no per-token payments to foreign providers.",
        },
      ],
    },
    {
      label: "Models",
      items: [
        {
          q: "Which models are in the catalog?",
          a: "Leading open international models from day one, and the catalog is continuously updated as the state of the art advances. We are actively supporting Danish Foundation Models in building Danish models on UCloud itself, models for Danish language, culture, and institutions, and they are on the way. Current availability is always shown in the workspace.",
        },
        {
          q: "What can I do with the API?",
          a: "AI is becoming a general-purpose technology for thinking and creating: it can read and summarise vast amounts of information, analyse data and images, write and improve text, generate software, and solve complex problems. It can also combine these capabilities, taking a vague idea, reasoning about it, searching for relevant information, and helping turn it into a finished result. Capabilities that once required teams of specialists are increasingly available to everyone, right beside your data. Because the UCloud AI speaks the standard OpenAI protocol, existing libraries, frameworks, and research notebooks work out of the box.",
        },
        {
          q: "Most of the leading models are developed in China/USA. How is that sovereign?",
          a: "An open-weight model is a published artifact, not a product. We run it ourselves on Bitten in Sønderborg, and it has no line back to its maker: no telemetry, no per-token payment, no way for your data to travel. Your tokens are computed in Denmark, under Danish law. That is what sovereignty means here: complete control of the model and the data it operates on.",
        },
        {
          q: "Why do Danish models matter?",
          a: "They are trained for Danish language, culture, and institutions, and they will be served in Denmark. For research on Danish corpora, they are the models you point at something Danish. Danish Foundation Models trains them on UCloud today, with national support. Bringing them into the catalog is the natural next step.",
        },
      ],
    },
    {
      label: "Security & residency",
      items: [
        {
          q: "Where is my data processed?",
          a: "In Denmark, on the Bitten national supercomputer in Sønderborg. No foreign APIs, and not a single token leaves the country. Users can optionally request resources on Gefion, the AI supercomputer provided by DCAI, still in Denmark and under the same guarantees.",
        },
        {
          q: "Is my data used for training?",
          a: "No. Your data is never used to train models, ours or anyone else's. Your research, your results, and your intellectual property remain yours alone. The guarantee holds for every request you send, sensitive research data included.",
        },
        {
          q: "Can I work with sensitive data?",
          a: "Yes. UCloud is ISO/IEC 27001:2022 certified and approved for sensitive personal data, and UCloud AI inherits that foundation. Processing stays within the EU, in full compliance with GDPR, and the platform meets the requirements of the NIS2 directive. Health data, register data, interview transcripts: work with them without sending them abroad.",
        },
      ],
    },
    {
      label: "Billing",
      items: [
        {
          q: "How am I billed?",
          a: "In credits, on the national UCloud platform. Your university purchases credits for AI usage as needed, and per-token usage lands in your project accounting, where you can see it. You can also purchase credits directly, going through your university's front office is not a requirement.",
        },
        {
          q: "Are there usage limits?",
          a: "Capacity is shared across the national UCloud platform, and allocations are managed by your university's front office, the same people who handle your compute and storage today. Rates per model are shown before you run. If you need credits, you can purchase them directly or through your university. There are no monthly limits or quotas beyond the credits you buy: your usage is bounded only by your balance.",
        },
        {
          q: "Why credits instead of per-token invoices?",
          a: "Because inference runs on the credit system of the national UCloud platform. All universities have access and can purchase credits for AI usage as they need them. There is no vendor to pay: no foreign invoices, no API subscriptions, no credit card. Usage appears in your project's accounting, where you can follow it like any other resource.",
        },
      ],
    },
  ],
};
